import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { colorForName, getInitials } from "@/components/card/SignersBubbles";
import { getWishItems, type WishItem, type Wishlist } from "@/services/wishlists";
import WishlistItemCard from "@/components/wishlist/WishlistItemCard";
import type { Tables } from "@/integrations/supabase/types";

interface SignerProfileSheetProps {
  contributorName: string;
  contributorEmail: string | null;
  contribution?: Tables<"contributions"> | null;
  onClose: () => void;
}

interface ProfileData {
  display_name: string | null;
  avatar_url: string | null;
  age: number | null;
  gender: string | null;
  preferences_wants: string | null;
  preferences_has: string | null;
  preferences_style: string | null;
  preferences_dislikes: string | null;
  preferences_price_guidance: string | null;
}

export default function SignerProfileSheet({
  contributorName,
  contributorEmail,
  contribution,
  onClose,
}: SignerProfileSheetProps) {
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [allItems, setAllItems] = useState<WishItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (!contributorEmail) {
        setLoading(false);
        return;
      }

      const normalizedEmail = contributorEmail.trim().toLowerCase();

      const { data: prof } = await supabase
        .from("profiles")
        .select("user_id, display_name, avatar_url, age, gender, preferences_wants, preferences_has, preferences_style, preferences_dislikes, preferences_price_guidance")
        .ilike("email", normalizedEmail)
        .maybeSingle();

      if (prof) {
        setProfile(prof as ProfileData);

        const { data: wls } = await supabase
          .from("wishlists" as any)
          .select("*")
          .eq("user_id", (prof as any).user_id)
          .order("created_at", { ascending: false });

        const lists = (wls as Wishlist[] | null) ?? [];

        const itemResults = await Promise.all(
          lists.map((wl) => getWishItems(wl.id))
        );
        setAllItems(itemResults.flatMap((r) => r.data || []));
      }

      setLoading(false);
    })();
  }, [contributorEmail]);

  const hasPreferences =
    profile &&
    (profile.preferences_wants || profile.preferences_has || profile.preferences_style || profile.preferences_dislikes || profile.preferences_price_guidance);

  const displayName = profile?.display_name || contributorName;
  const genderSymbol = profile?.gender?.toLowerCase() === "male" ? "♂" : profile?.gender?.toLowerCase() === "female" ? "♀" : null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] flex items-center justify-center"
        onClick={onClose}
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 28, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full h-full sm:w-[390px] sm:h-[85vh] sm:max-h-[844px] sm:rounded-2xl overflow-hidden bg-background flex flex-col"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-background/80 backdrop-blur flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
          >
            <X size={18} />
          </button>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto">
            {/* Hero */}
            <div className="relative px-6 pt-16 pb-8 flex flex-col items-center text-center">
              <div
                className="absolute top-0 left-1/2 -translate-x-1/2 w-[280px] h-[200px] rounded-full opacity-30 blur-3xl pointer-events-none"
                style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.4), transparent 70%)" }}
              />

              {/* Avatar */}
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 18 }}
                className="relative w-24 h-24 rounded-full ring-4 ring-background shadow-xl overflow-hidden mb-5"
              >
                {profile?.avatar_url ? (
                  <img src={profile.avatar_url} alt={displayName} className="w-full h-full object-cover" />
                ) : (
                  <div className={`w-full h-full flex items-center justify-center text-3xl font-bold ${colorForName(contributorName)}`}>
                    {getInitials(contributorName)}
                  </div>
                )}
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-2xl font-bold text-foreground font-serif flex items-center gap-1.5"
              >
                {displayName}
                {genderSymbol && (
                  <span className="text-lg text-muted-foreground font-normal">{genderSymbol}</span>
                )}
              </motion.h2>

              {profile?.age && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.15 }}
                  className="text-xs text-muted-foreground mt-1"
                >
                  {profile.age} years old
                </motion.p>
              )}

              {contribution && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                  className="mt-4 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold"
                >
                  💰 Chipped in ${Number(contribution.amount).toFixed(0)}
                </motion.div>
              )}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 size={24} className="animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="px-5 pb-8 space-y-8">
                {/* Gift Preferences */}
                {hasPreferences && (
                  <section>
                    <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground mb-3">
                      Gift Preferences
                    </p>
                    <div className="space-y-2">
                      {profile?.preferences_wants && (
                        <PreferenceRow label="Loves & Wants" value={profile.preferences_wants} />
                      )}
                      {profile?.preferences_has && (
                        <PreferenceRow label="Already Has" value={profile.preferences_has} />
                      )}
                      {profile?.preferences_style && (
                        <PreferenceRow label="Style & Taste" value={profile.preferences_style} />
                      )}
                      {profile?.preferences_dislikes && (
                        <PreferenceRow label="Dislikes" value={profile.preferences_dislikes} />
                      )}
                      {profile?.preferences_price_guidance && (
                        <PreferenceRow label="Budget Range" value={profile.preferences_price_guidance} />
                      )}
                    </div>
                  </section>
                )}

                {/* Wishlist Items */}
                {allItems.length > 0 && (
                  <section>
                    <div className="flex items-baseline justify-between mb-3">
                      <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
                        Wishlist
                      </p>
                      <span className="text-[10px] text-muted-foreground">
                        {allItems.length} {allItems.length === 1 ? "item" : "items"}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2.5">
                      {allItems.map((item) => (
                        <WishlistItemCard
                          key={item.id}
                          item={item}
                          isOwner={false}
                          isAuthenticated={false}
                          compact
                        />
                      ))}
                    </div>
                  </section>
                )}

                {/* Empty state */}
                {!hasPreferences && allItems.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-14 text-center">
                    <p className="text-4xl mb-4">🎁</p>
                    <p className="text-sm font-medium text-muted-foreground">
                      No profile details yet
                    </p>
                    <p className="text-xs text-muted-foreground/60 mt-1 max-w-[220px]">
                      {contributorName} hasn't set up their preferences or wishlists
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="pb-[env(safe-area-inset-bottom)]" />
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function PreferenceRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-muted/40 px-4 py-3">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1">
        {label}
      </p>
      <p className="text-[13px] text-foreground leading-relaxed">{value}</p>
    </div>
  );
}
